import React from 'react';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';
import SelectionCard from '../SelectionCard';

function Step5TargetAudience() {
  const { formData, updateFormData } = useForm();
  const stepData = formData.step5;

  const handleInputChange = (field, value) => {
    updateFormData('step5', { [field]: value });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = stepData[field] || [];
    const updatedArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    handleInputChange(field, updatedArray);
  };

  const ageRanges = [
    '18-25', '26-35', '36-45', '46-55', '56-65', '65+'
  ];

  const geographicOptions = [
    'Local (city/region)', 'National', 'International', 'Online only'
  ];

  const customerTypes = [
    {
      id: 'B2B',
      title: 'Business to Business (B2B)',
      description: 'You sell products or services to other businesses. Focus on professional relationships and value-driven solutions that enhance business operations and drive growth for your corporate clients.'
    },
    {
      id: 'B2C',
      title: 'Business to Consumer (B2C)',
      description: 'You sell products or services directly to individual customers. Create engaging experiences that resonate with personal needs and emotions, building lasting connections through user-friendly interactions.'
    },
    {
      id: 'Both',
      title: 'Both B2B and B2C',
      description: 'You serve both businesses and individual customers. Balance professional credibility with approachable appeal, ensuring your messaging and design effectively communicate to diverse audiences.'
    },
    {
      id: 'Other',
      title: 'Other',
      description: 'You serve either NGOs, government, or a niche market. Address the unique requirements of specialised audiences with tailored communication that understands their specific challenges and priorities.'
    }
  ];

  const audienceTones = [
    { id: 'professional-authoritative', title: 'Professional and authoritative' },
    { id: 'formal-respectful', title: 'Formal and respectful' },
    { id: 'informative-straightforward', title: 'Informative and straightforward' },
    { id: 'minimalist-concise', title: 'Minimalist and concise' },
    { id: 'friendly-conversational', title: 'Friendly and conversational' },
    { id: 'warm-welcoming', title: 'Warm and welcoming' },
    { id: 'calm-reassuring', title: 'Calm and reassuring' },
    { id: 'informal-approachable', title: 'Informal and approachable' },
    { id: 'youthful-energetic', title: 'Youthful and energetic' },
    { id: 'optimistic-encouraging', title: 'Optimistic and encouraging' },
    { id: 'playful-witty', title: 'Playful or witty' },
    { id: 'dramatic-bold', title: 'Dramatic and bold' }
  ];

  const websiteGoals = [
    { id: 'generate-leads', title: 'Generate leads or enquiries' },
    { id: 'increase-sales', title: 'Increase online sales or bookings' },
    { id: 'build-awareness', title: 'Build brand awareness' },
    { id: 'educate-inform', title: 'Educate or inform visitors' },
    { id: 'showcase-portfolio', title: 'Showcase portfolio or past work' },
    { id: 'provide-support', title: 'Provide customer support or resources' },
    { id: 'increase-traffic', title: 'Increase website traffic' },
    { id: 'expand-email-base', title: 'Expand email subscriber base' },
    { id: 'collect-feedback', title: 'Collect customer feedback and insights' }
  ];

  const ctaPreferences = [
    { id: 'contact-us', title: 'Contact us' },
    { id: 'request-quote', title: 'Request a quote' },
    { id: 'sign-up-newsletter', title: 'Sign up for a newsletter' },
    { id: 'book-appointment', title: 'Book an appointment' },
    { id: 'shop-online', title: 'Shop online' },
    { id: 'download-brochure', title: 'Download a brochure or guide' },
    { id: 'start-chat', title: 'Start a live chat or conversation' },
    { id: 'follow-social', title: 'Follow us on social media' }
  ];

  const handleToneToggle = (toneId) => {
    const currentTones = stepData.audienceTone || [];
    if (currentTones.includes(toneId)) {
      handleInputChange('audienceTone', currentTones.filter(id => id !== toneId));
    } else if (currentTones.length < 3) {
      handleInputChange('audienceTone', [...currentTones, toneId]);
    } else {
      alert('You can select up to three tones');
    }
  };

  return (
    <StepLayout
      title="Let's meet the people who matter most! 👋"
      stepNumber={5}
      totalSteps={7}
      nextButtonText="Let's handle the tech"
      nextStep={6}
      prevStep={4}
    >

      <div className="p-4 bg-[#F9FAFB] border-l-4 border-[#FF5841] rounded-lg mb-8">
        <p className="text-gray-700 leading-relaxed">
          Understanding your audience is like having a compass for your website's journey. The more we know about the wonderful people you serve, the better we can craft an experience that truly resonates with them and turns visitors into loyal supporters.
        </p>
      </div>

      <div>
        {/* Audience Profile */}
        <div className="mb-20 last:mb-0">
          <h3 className="text-xl font-medium text-gray-900 mb-6">Audience profile</h3>
          
          {/* Age Range */}
          <div className="mb-6">
            <h4 className="font-medium text-17px text-gray-900 mb-4">Primary age range of your customers</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {ageRanges.map((range) => (
                <button
                  key={range}
                  onClick={() => handleArrayToggle('primaryAgeRange', range)}
                  className={`px-4 py-3 text-16px font-medium rounded-lg border-2 transition-all duration-200 ${
                    (stepData.primaryAgeRange || []).includes(range)
                      ? 'bg-[#FFEDEB] border-[#FF5841] text-[#FF5841]'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>

          {/* Geographic Focus */}
          <div className="mb-6">
            <h4 className="font-medium text-17px text-gray-900 mb-4 mt-12">Geographic focus (Select all that apply)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {geographicOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => handleArrayToggle('geographicFocus', option)}
                  className={`px-4 py-3 text-16px font-medium rounded-lg border-2 text-left transition-all duration-200 ${
                    (stepData.geographicFocus || []).includes(option)
                      ? 'bg-[#FFEDEB] border-[#FF5841] text-[#FF5841]'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            
            {/* Local Region Input */}
            {(stepData.geographicFocus || []).includes('Local (city/region)') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Specify city, town or region
                </label>
                <input
                  type="text"
                  value={stepData.localRegion || ''}
                  onChange={(e) => handleInputChange('localRegion', e.target.value)}
                  placeholder="Enter your local area"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Customer Type */}
          <div>
            <h4 className="font-medium text-17px text-gray-900 mb-4 mt-12">Customer type</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {customerTypes.map((type) => (
                <SelectionCard
                  key={type.id}
                  title={type.title}
                  description={type.description}
                  selected={stepData.customerType === type.id}
                  onClick={() => handleInputChange('customerType', type.id)}
                />
              ))}
            </div>
            
            {/* Other Customer Type Input */}
            {stepData.customerType === 'other' && (
              <div className="mt-4">
                <label className="block text-16px font-medium text-gray-900 mb-2">
                  Please specify your customer type below
                </label>
                <textarea
                  value={stepData.otherCustomerType || ''}
                  onChange={(e) => handleInputChange('otherCustomerType', e.target.value)}
                  placeholder="Describe your specific customer type..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
                />
              </div>
            )}
          </div>

          {/* Typical audience challenges */}
          <div>
            <h4 className="font-medium text-17px text-gray-900 mb-4 mt-12">Typical audience challenges</h4>
            <div>
              <label className="block text-16px font-medium text-gray-600 mb-2">
                Describe common customer pain points and how you address them.
              </label>
              <textarea
                value={stepData.audienceChallenges || ''}
                onChange={(e) => handleInputChange('audienceChallenges', e.target.value)}
                placeholder="What problems do your customers face, and how does your business solve these challenges? Be specific about pain points and your solutions..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Audience Tone and Messaging */}
          <div>
            <h4 className="font-medium mt-12 text-17px text-gray-900 mb-2">Audience Tone and Messaging</h4>
            <p className="text-16px text-gray-600 mb-4">How should your website communicate with the audience? Select the tone or style your website should use when addressing visitors. You may select up to three complementary tones.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {audienceTones.map((tone) => (
                <SelectionCard
                  key={tone.id}
                  title={tone.title}
                  selected={(stepData.audienceTone || []).includes(tone.id)}
                  onClick={() => handleToneToggle(tone.id)}
                  className="text-center"
                />
              ))}
            </div>
          </div>

          {/* Primary website goals */}
          <div>
            <h4 className="font-medium mt-12 text-17px text-gray-900 mb-4">Primary website goals</h4>
            <p className="text-16px text-gray-600 mb-4">What are your main goals for the website? Select your key objectives.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {websiteGoals.map((goal) => (
                <SelectionCard
                  key={goal.id}
                  title={goal.title}
                  selected={(stepData.websiteGoals || []).includes(goal.id)}
                  onClick={() => handleArrayToggle('websiteGoals', goal.id)}
                  className="text-center"
                />
              ))}
            </div>
          </div>

          {/* Call-to-Action (CTA) Preferences */}
          <div>
            <h4 className="font-medium text-17px mt-12 text-gray-900 mb-4">Call-to-Action (CTA) Preferences</h4>
            <p className="text-16px text-gray-600 mb-4">What actions do you want visitors to take on your website?</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {ctaPreferences.map((cta) => (
                <SelectionCard
                  key={cta.id}
                  title={cta.title}
                  selected={(stepData.ctaPreferences || []).includes(cta.id)}
                  onClick={() => handleArrayToggle('ctaPreferences', cta.id)}
                  className="text-center"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}

export default Step5TargetAudience;